// /apps/patient/src/pages/PatientOnboarding.tsx

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePatient } from '../contexts/PatientContext';
import { patientApiService } from '../services/api';
import { getCurrentPlatform, getEnvironmentConfig } from '../config/platforms';
import { ONBOARDING_FORM_BACKUP_KEY, waitForExerciseSubscription } from '../utils/subscriptionPoll';
import {
    ArrowLeft,
    Check,
    Loader2,
    Shield,
    Bell,
    Mail,
    MessageSquare,
    Dumbbell,
    Heart,
    AlertTriangle,
    CreditCard
} from 'lucide-react';
import { initializeFaceTecSDK } from '@prism-ehr/shared/services/faceTecService';
import { EnrollmentProcessor } from '@prism-ehr/shared/processors/EnrollmentProcessor';
import type { Patient, UserProfile } from '@prism-ehr/shared';

// Define form data interface extending Patient
interface OnboardingFormData extends Partial<Patient> {
    // Notification preferences (from user)
    notification_preferences?: boolean;
    email_preferences?: boolean;
    sms_preferences?: boolean;

    // Biometric MFA status
    biometric_mfa?: boolean;

    // Terms acceptance
    accepted_terms?: boolean;
    accepted_privacy?: boolean;
    accepted_hipaa?: boolean;

    // Temporary fields for emergency contact (before structuring)
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    emergency_contact_relationship?: string;

    // Lifestyle fields (flattened for form)
    sleep_hours?: number;
    sleep_quality?: 'poor' | 'fair' | 'good' | 'excellent';
    diet_tracking_level?: 'none' | 'basic' | 'detailed' | 'macros';
    eating_pattern?: string;
    meals_per_day?: number;
    water_intake?: number;
    daily_calories?: number;
    protein_grams?: number;
    carbs_grams?: number;
    fat_grams?: number;
    stress_level?: 'low' | 'moderate' | 'high' | 'severe';
    activity_level?: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active';

    // Fitness fields (flattened)
    fitness_goals?: string[];
    fitness_level?: 'beginner' | 'intermediate' | 'advanced';
    physical_condition?: 'very_low' | 'low' | 'moderate' | 'good' | 'high';
    preferred_duration?: number;
    available_equipment?: string[];
    physical_limitations?: string;

    // Provider info (for display)
    provider_name?: string;
    organization_name?: string;

    // Type flag
    isProviderLinked?: boolean;

    // NEW: Health screening fields (for exercise platform)
    has_heart_condition?: boolean;
    has_chest_pain_during_activity?: boolean;
    has_chest_pain_at_rest?: boolean;
    has_balance_or_dizziness?: boolean;
    has_bone_or_joint_problems?: boolean;
    takes_blood_pressure_medication?: boolean;
    has_other_medical_reasons?: boolean;
    other_medical_details?: string;
    has_current_injuries?: boolean;
    current_injuries?: string[];
    has_chronic_conditions?: boolean;
    chronic_conditions?: string[];
    chronic_condition_details?: { condition: string; details: string }[];
    physician_clearance_obtained?: boolean;

    // NEW: Extended fitness profile fields
    cardio_preferred_activities?: string[];
    outdoor_preference?: 'indoor_only' | 'outdoor_only' | 'both';
    preferred_workout_types?: string[];
    preferred_days?: number[];
    preferred_time_of_day?: 'morning' | 'afternoon' | 'evening' | 'flexible';
    sessions_per_week?: number;

    // NEW: Health disclaimer for exercise platform
    accepted_health_disclaimer?: boolean;
}


// Step Components
const ProfileBasicsStep = ({
    formData,
    updateFormData,
    errors,
    isProviderLinked
}) => (
    <div className="space-y-4">
        <h2 className="text-xl font-semibold mb-4">Welcome! Let's complete your profile</h2>

        <div className="grid grid-cols-2 gap-3">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name*
                </label>
                <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => updateFormData({ first_name: e.target.value })}
                    className={`w-full p-3 border rounded-lg ${errors.first_name ? 'border-red-500' : 'border-gray-200'}`}
                />
                {errors.first_name && (
                    <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name*
                </label>
                <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => updateFormData({ last_name: e.target.value })}
                    className={`w-full p-3 border rounded-lg ${errors.last_name ? 'border-red-500' : 'border-gray-200'}`}
                />
                {errors.last_name && (
                    <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
                )}
            </div>
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number*
            </label>
            <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData({ phone: e.target.value })}
                className={`w-full p-3 border rounded-lg ${errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                placeholder="(555) 123-4567"
            />
            {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Pronouns
            </label>
            <select
                value={formData.preferred_pronouns || ''}
                onChange={(e) => updateFormData({ preferred_pronouns: e.target.value })}
                className="w-full p-3 border rounded-lg border-gray-200"
            >
                <option value="">Select pronouns (optional)</option>
                <option value="he/him">he/him</option>
                <option value="she/her">she/her</option>
                <option value="they/them">they/them</option>
                <option value="other">other</option>
            </select>
        </div>

        {/* Notification Preferences */}
        <div className="border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Notification Preferences</h3>

            <label className="flex items-start space-x-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={formData.notification_preferences}
                    onChange={(e) => updateFormData({ notification_preferences: e.target.checked })}
                    className="mt-1 rounded border-gray-300 text-primary"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium">Push Notifications</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Receive reminders and important updates
                    </p>
                </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={formData.email_preferences}
                    onChange={(e) => updateFormData({ email_preferences: e.target.checked })}
                    className="mt-1 rounded border-gray-300 text-primary"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium">Email Notifications</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Get updates and reminders via email
                    </p>
                </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={formData.sms_preferences}
                    onChange={(e) => updateFormData({ sms_preferences: e.target.checked })}
                    className="mt-1 rounded border-gray-300 text-primary"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium">SMS Notifications</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Message & data rates may apply
                    </p>
                </div>
            </label>
        </div>

        {isProviderLinked && formData.provider_name && (
            <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    You're connected with <strong>{formData.provider_name}</strong>
                    {formData.organization_name && ` at ${formData.organization_name}`}
                </p>
            </div>
        )}
    </div>
);

const LifestyleStep = ({ formData, updateFormData, errors }) => {
    const [dietDetailLevel, setDietDetailLevel] = useState(
        formData.diet_tracking_level || 'basic'
    );

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-4">Lifestyle & Wellness</h2>

            {/* Sleep */}
            <div>
                <h3 className="text-base font-medium mb-3">Sleep</h3>
                <div className="space-y-3">
                    <div>
                        <label className="text-sm text-gray-700">Average hours per night</label>
                        <select
                            value={formData.sleep_hours || ''}
                            onChange={(e) => updateFormData({ sleep_hours: parseFloat(e.target.value) })}
                            className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                        >
                            <option value="">Select hours</option>
                            {[4, 5, 6, 7, 8, 9, 10, 11, 12].map(h => (
                                <option key={h} value={h}>{h} hours</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="text-sm text-gray-700">Sleep quality</label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                            {['poor', 'fair', 'good', 'excellent'].map(quality => (
                                <button
                                    key={quality}
                                    type="button"
                                    onClick={() => updateFormData({ sleep_quality: quality })}
                                    className={`p-2 rounded-lg border capitalize text-sm ${formData.sleep_quality === quality
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200'
                                        }`}
                                >
                                    {quality}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Diet */}
            <div>
                <h3 className="text-base font-medium mb-3">Nutrition</h3>

                {/* Diet tracking level selector */}
                <div className="mb-4">
                    <label className="text-sm text-gray-700">How detailed would you like to track nutrition?</label>
                    <div className="space-y-2 mt-2">
                        {[
                            { value: 'none', label: 'I prefer not to track' },
                            { value: 'basic', label: 'Basic (eating patterns)' },
                            { value: 'detailed', label: 'Detailed (meals, water)' },
                            { value: 'macros', label: 'Full macros & calories' }
                        ].map(option => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                    setDietDetailLevel(option.value);
                                    updateFormData({ diet_tracking_level: option.value });
                                }}
                                className={`w-full p-3 rounded-lg border text-left text-sm ${dietDetailLevel === option.value
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-gray-200'
                                    }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Show fields based on selected level */}
                {dietDetailLevel === 'basic' && (
                    <div className="space-y-3">
                        <div>
                            <label className="text-sm text-gray-700">Eating pattern</label>
                            <select
                                value={formData.eating_pattern || ''}
                                onChange={(e) => updateFormData({ eating_pattern: e.target.value })}
                                className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                            >
                                <option value="">Select pattern</option>
                                <option value="regular">Regular meals</option>
                                <option value="irregular">Irregular schedule</option>
                                <option value="restricted">Time-restricted</option>
                            </select>
                        </div>
                    </div>
                )}

                {dietDetailLevel === 'detailed' && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm text-gray-700">Meals per day</label>
                                <input
                                    type="number"
                                    value={formData.meals_per_day || ''}
                                    onChange={(e) => updateFormData({ meals_per_day: parseInt(e.target.value) })}
                                    className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                                    min="1"
                                    max="6"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-700">Water (glasses/day)</label>
                                <input
                                    type="number"
                                    value={formData.water_intake || ''}
                                    onChange={(e) => updateFormData({ water_intake: parseInt(e.target.value) })}
                                    className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                                    min="0"
                                    max="20"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {dietDetailLevel === 'macros' && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm text-gray-700">Daily calories</label>
                                <input
                                    type="number"
                                    value={formData.daily_calories || ''}
                                    onChange={(e) => updateFormData({ daily_calories: parseInt(e.target.value) })}
                                    className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                                    placeholder="2000"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-700">Protein (g)</label>
                                <input
                                    type="number"
                                    value={formData.protein_grams || ''}
                                    onChange={(e) => updateFormData({ protein_grams: parseInt(e.target.value) })}
                                    className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                                    placeholder="50"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm text-gray-700">Carbs (g)</label>
                                <input
                                    type="number"
                                    value={formData.carbs_grams || ''}
                                    onChange={(e) => updateFormData({ carbs_grams: parseInt(e.target.value) })}
                                    className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                                    placeholder="250"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-700">Fat (g)</label>
                                <input
                                    type="number"
                                    value={formData.fat_grams || ''}
                                    onChange={(e) => updateFormData({ fat_grams: parseInt(e.target.value) })}
                                    className="w-full p-3 border rounded-lg border-gray-200 mt-1"
                                    placeholder="65"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Stress */}
            <div>
                <h3 className="text-base font-medium mb-3">Stress Level</h3>
                <div className="grid grid-cols-2 gap-2">
                    {['low', 'moderate', 'high', 'severe'].map(level => (
                        <button
                            key={level}
                            type="button"
                            onClick={() => updateFormData({ stress_level: level })}
                            className={`p-3 rounded-lg border capitalize text-sm ${formData.stress_level === level
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200'
                                }`}
                        >
                            {level}
                        </button>
                    ))}
                </div>
            </div>

            {/* Activity */}
            <div>
                <h3 className="text-base font-medium mb-3">Current Activity Level</h3>
                <div className="space-y-2">
                    {[
                        { value: 'sedentary', label: 'Sedentary (little to no exercise)' },
                        { value: 'lightly_active', label: 'Lightly Active (1-3 days/week)' },
                        { value: 'moderately_active', label: 'Moderately Active (3-5 days/week)' },
                        { value: 'very_active', label: 'Very Active (6-7 days/week)' }
                    ].map(option => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => updateFormData({ activity_level: option.value })}
                            className={`w-full p-3 rounded-lg border text-left text-sm ${formData.activity_level === option.value
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200'
                                }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const MedicalInfoStep = ({
    formData,
    updateFormData,
    errors
}) => {
    const addItem = (field, newItem) => {
        const currentItems = formData[field] || [];
        updateFormData({ [field]: [...currentItems, newItem] });
    };

    const removeItem = (field, index) => {
        const currentItems = formData[field] || [];
        updateFormData({
            [field]: currentItems.filter((_, i) => i !== index)
        });
    };

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-4">Medical Information</h2>

            {/* Allergies */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allergies
                </label>
                <div className="space-y-2">
                    {(formData.allergies || []).map((allergy, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                            <span className="flex-1 text-sm">{allergy.substance}</span>
                            <button
                                type="button"
                                onClick={() => removeItem('allergies', index)}
                                className="text-red-500 text-sm"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            const substance = prompt('Enter allergy:');
                            if (substance) {
                                addItem('allergies', {
                                    substance,
                                    severity: 'moderate',
                                    reaction: ''
                                });
                            }
                        }}
                        className="text-primary text-sm font-medium"
                    >
                        + Add Allergy
                    </button>
                </div>
            </div>

            {/* Current Medications */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Medications
                </label>
                <div className="space-y-2">
                    {(formData.current_medications || []).map((med, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                            <span className="flex-1 text-sm">
                                {med.name} {med.dosage && `- ${med.dosage}`}
                            </span>
                            <button
                                type="button"
                                onClick={() => removeItem('current_medications', index)}
                                className="text-red-500 text-sm"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            const name = prompt('Enter medication name:');
                            if (name) {
                                const dosage = prompt('Enter dosage (optional):');
                                addItem('current_medications', {
                                    name,
                                    dosage,
                                    is_active: true
                                });
                            }
                        }}
                        className="text-primary text-sm font-medium"
                    >
                        + Add Medication
                    </button>
                </div>
            </div>

            {/* Medical History */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical History
                </label>
                <div className="space-y-2">
                    {(formData.medical_history || []).map((condition, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                            <span className="flex-1 text-sm">{condition.condition}</span>
                            <button
                                type="button"
                                onClick={() => removeItem('medical_history', index)}
                                className="text-red-500 text-sm"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            const condition = prompt('Enter medical condition:');
                            if (condition) {
                                addItem('medical_history', {
                                    condition,
                                    status: 'active'
                                });
                            }
                        }}
                        className="text-primary text-sm font-medium"
                    >
                        + Add Condition
                    </button>
                </div>
            </div>

            {/* Emergency Contact */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emergency Contact
                </label>
                <div className="space-y-3">
                    <input
                        type="text"
                        placeholder="Contact name"
                        value={formData.emergency_contact_name || ''}
                        onChange={(e) => updateFormData({ emergency_contact_name: e.target.value })}
                        className="w-full p-3 border rounded-lg border-gray-200"
                    />
                    <input
                        type="tel"
                        placeholder="Contact phone"
                        value={formData.emergency_contact_phone || ''}
                        onChange={(e) => updateFormData({ emergency_contact_phone: e.target.value })}
                        className="w-full p-3 border rounded-lg border-gray-200"
                    />
                    <input
                        type="text"
                        placeholder="Relationship"
                        value={formData.emergency_contact_relationship || ''}
                        onChange={(e) => updateFormData({ emergency_contact_relationship: e.target.value })}
                        className="w-full p-3 border rounded-lg border-gray-200"
                    />
                </div>
            </div>
        </div>
    );
};

const HealthScreeningStep = ({
    formData,
    updateFormData,
    errors
}: {
    formData: OnboardingFormData;
    updateFormData: (updates: Partial<OnboardingFormData>) => void;
    errors: Record<string, string>;
}) => {
    const parqQuestions = [
        {
            key: 'has_heart_condition',
            question: 'Has your doctor ever said that you have a heart condition and that you should only do physical activity recommended by a doctor?'
        },
        {
            key: 'has_chest_pain_during_activity',
            question: 'Do you feel pain in your chest when you do physical activity?'
        },
        {
            key: 'has_chest_pain_at_rest',
            question: 'In the past month, have you had chest pain when you were not doing physical activity?'
        },
        {
            key: 'has_balance_or_dizziness',
            question: 'Do you lose your balance because of dizziness or do you ever lose consciousness?'
        },
        {
            key: 'has_bone_or_joint_problems',
            question: 'Do you have a bone or joint problem that could be made worse by a change in your physical activity?'
        },
        {
            key: 'takes_blood_pressure_medication',
            question: 'Is your doctor currently prescribing drugs for your blood pressure or heart condition?'
        },
        {
            key: 'has_other_medical_reasons',
            question: 'Do you know of any other reason why you should not do physical activity?'
        }
    ];

    // Check if any PAR-Q question is answered yes
    const hasPositiveParq = parqQuestions.some(q => formData[q.key as keyof OnboardingFormData] === true);

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold mb-2">Health Screening</h2>
                <p className="text-sm text-gray-600">
                    Please answer these questions honestly to help us create a safe exercise program for you.
                </p>
            </div>

            {/* PAR-Q Questions */}
            <div className="space-y-4">
                <h3 className="text-base font-medium text-gray-900">Physical Activity Readiness</h3>

                {parqQuestions.map((q) => (
                    <div key={q.key} className="border rounded-lg p-4">
                        <p className="text-sm text-gray-700 mb-3">{q.question}</p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => updateFormData({ [q.key]: true })}
                                className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${formData[q.key as keyof OnboardingFormData] === true
                                    ? 'border-red-500 bg-red-50 text-red-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                Yes
                            </button>
                            <button
                                type="button"
                                onClick={() => updateFormData({ [q.key]: false })}
                                className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${formData[q.key as keyof OnboardingFormData] === false
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                No
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Warning if any positive answers */}
            {hasPositiveParq && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <h4 className="text-sm font-medium text-amber-800">Medical Clearance Recommended</h4>
                            <p className="text-sm text-amber-700 mt-1">
                                Based on your answers, we recommend consulting with a healthcare provider before starting an exercise program.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Other medical details if applicable */}
            {formData.has_other_medical_reasons && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Please provide details
                    </label>
                    <textarea
                        value={formData.other_medical_details || ''}
                        onChange={(e) => updateFormData({ other_medical_details: e.target.value })}
                        className="w-full p-3 border rounded-lg border-gray-200"
                        rows={3}
                        placeholder="Describe any medical conditions or concerns..."
                    />
                </div>
            )}

            {/* Current Injuries */}
            <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-900">Current Injuries</h3>

                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={() => updateFormData({ has_current_injuries: true })}
                        className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-colors ${formData.has_current_injuries === true
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                    >
                        Yes, I have injuries
                    </button>
                    <button
                        type="button"
                        onClick={() => updateFormData({ has_current_injuries: false, current_injuries: [] })}
                        className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-colors ${formData.has_current_injuries === false
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                    >
                        No current injuries
                    </button>
                </div>

                {formData.has_current_injuries && (
                    <div>
                        <label className="block text-sm text-gray-700 mb-1">
                            What injuries do you have?
                        </label>
                        <textarea
                            value={(formData.current_injuries || []).join('\n')}
                            onChange={(e) => updateFormData({
                                current_injuries: e.target.value.split('\n').filter(Boolean)
                            })}
                            className="w-full p-3 border rounded-lg border-gray-200"
                            rows={3}
                            placeholder="List your injuries (one per line)..."
                        />
                    </div>
                )}
            </div>

            {/* Chronic Conditions */}
            <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-900">Chronic Conditions</h3>

                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={() => updateFormData({ has_chronic_conditions: true })}
                        className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-colors ${formData.has_chronic_conditions === true
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        onClick={() => updateFormData({ has_chronic_conditions: false, chronic_conditions: [] })}
                        className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-colors ${formData.has_chronic_conditions === false
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                    >
                        No
                    </button>
                </div>

                {formData.has_chronic_conditions && (
                    <div>
                        <label className="block text-sm text-gray-700 mb-2">
                            Select any conditions that apply and provide details:
                        </label>
                        <div className="space-y-2">
                            {[
                                'Diabetes',
                                'High Blood Pressure',
                                'Asthma',
                                'Arthritis',
                                'Back Pain',
                                'Obesity',
                                'Other'
                            ].map(condition => {
                                const isSelected = (formData.chronic_conditions || []).includes(condition);
                                const detailEntry = (formData.chronic_condition_details || []).find(d => d.condition === condition);
                                return (
                                    <div key={condition}>
                                        <label className="flex items-center space-x-3 cursor-pointer p-2">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => {
                                                    const current = formData.chronic_conditions || [];
                                                    const currentDetails = formData.chronic_condition_details || [];
                                                    if (current.includes(condition)) {
                                                        updateFormData({
                                                            chronic_conditions: current.filter(c => c !== condition),
                                                            chronic_condition_details: currentDetails.filter(d => d.condition !== condition)
                                                        });
                                                    } else {
                                                        updateFormData({
                                                            chronic_conditions: [...current, condition],
                                                            chronic_condition_details: [...currentDetails, { condition, details: '' }]
                                                        });
                                                    }
                                                }}
                                                className="rounded border-gray-300 text-primary"
                                            />
                                            <span className="text-sm">{condition}</span>
                                        </label>
                                        {isSelected && (
                                            <div className="ml-9 mt-1 mb-2">
                                                <input
                                                    type="text"
                                                    value={detailEntry?.details || ''}
                                                    onChange={(e) => {
                                                        const currentDetails = formData.chronic_condition_details || [];
                                                        const updated = currentDetails.map(d =>
                                                            d.condition === condition ? { ...d, details: e.target.value } : d
                                                        );
                                                        if (!currentDetails.find(d => d.condition === condition)) {
                                                            updated.push({ condition, details: e.target.value });
                                                        }
                                                        updateFormData({ chronic_condition_details: updated });
                                                    }}
                                                    className="w-full p-2 border rounded-lg border-gray-200 text-sm"
                                                    placeholder={`Type, severity, affected areas, or other details about your ${condition.toLowerCase()}...`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Physician Clearance */}
            {hasPositiveParq && (
                <div className="border rounded-lg p-4">
                    <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={formData.physician_clearance_obtained || false}
                            onChange={(e) => updateFormData({ physician_clearance_obtained: e.target.checked })}
                            className="mt-1 rounded border-gray-300 text-primary"
                        />
                        <div>
                            <span className="text-sm font-medium">I have received physician clearance to exercise</span>
                            <p className="text-xs text-gray-500 mt-1">
                                Check this box if you've already consulted with a healthcare provider
                            </p>
                        </div>
                    </label>
                </div>
            )}

            {errors.health_screening && (
                <p className="text-red-500 text-sm">{errors.health_screening}</p>
            )}
        </div>
    );
};

export const FitnessProfileStep = ({
    formData,
    updateFormData,
    errors,
    isExercisePlatform = false  // NEW: pass this prop
}) => {
    const fitnessGoals = [
        { value: 'weight_loss', label: 'Weight Loss', icon: '🔥' },
        { value: 'muscle_gain', label: 'Build Muscle', icon: '💪' },
        { value: 'endurance', label: 'Endurance', icon: '🏃' },
        { value: 'strength', label: 'Strength', icon: '🏋️' },
        { value: 'flexibility', label: 'Flexibility', icon: '🧘' },
        { value: 'general_fitness', label: 'General Fitness', icon: '❤️' },
        // NEW goals for exercise platform
        ...(isExercisePlatform ? [
            { value: 'sport_specific', label: 'Sport Performance', icon: '🏆' },
            { value: 'stress_relief', label: 'Stress Relief', icon: '😌' },
            { value: 'improve_energy', label: 'More Energy', icon: '⚡' }
        ] : [])
    ];

    const equipmentOptions = [
        'Dumbbells',
        'Resistance Bands',
        'Pull-up Bar',
        'Yoga Mat',
        'Kettlebells',
        'Barbell',
        'Bench',
        'Cardio Machine',
        'None - Bodyweight Only'
    ];

    // NEW: Cardio activity options
    const cardioActivities = [
        { value: 'running', label: 'Running', icon: '🏃' },
        { value: 'cycling', label: 'Cycling', icon: '🚴' },
        { value: 'swimming', label: 'Swimming', icon: '🏊' },
        { value: 'walking', label: 'Walking', icon: '🚶' },
        { value: 'hiking', label: 'Hiking', icon: '🥾' },
        { value: 'rowing', label: 'Rowing', icon: '🚣' },
        { value: 'elliptical', label: 'Elliptical', icon: '⭕' },
        { value: 'jump_rope', label: 'Jump Rope', icon: '🪢' },
        { value: 'dancing', label: 'Dancing', icon: '💃' },
        { value: 'sports', label: 'Sports', icon: '⚽' }
    ];

    // NEW: Workout type options
    const workoutTypes = [
        { value: 'strength', label: 'Strength Training' },
        { value: 'cardio', label: 'Cardio' },
        { value: 'hiit', label: 'HIIT' },
        { value: 'yoga', label: 'Yoga' },
        { value: 'pilates', label: 'Pilates' },
        { value: 'functional', label: 'Functional Training' },
        { value: 'bodyweight', label: 'Bodyweight' },
        { value: 'circuit', label: 'Circuit Training' }
    ];

    // NEW: Days of week
    const daysOfWeek = [
        { value: 1, label: 'Mon' },
        { value: 2, label: 'Tue' },
        { value: 3, label: 'Wed' },
        { value: 4, label: 'Thu' },
        { value: 5, label: 'Fri' },
        { value: 6, label: 'Sat' },
        { value: 7, label: 'Sun' }
    ];

    const toggleGoal = (goal: string) => {
        const currentGoals = formData.fitness_goals || [];
        const newGoals = currentGoals.includes(goal)
            ? currentGoals.filter(g => g !== goal)
            : [...currentGoals, goal];
        updateFormData({ fitness_goals: newGoals });
    };

    const toggleEquipment = (equipment: string) => {
        const currentEquipment = formData.available_equipment || [];
        const newEquipment = currentEquipment.includes(equipment)
            ? currentEquipment.filter(e => e !== equipment)
            : [...currentEquipment, equipment];
        updateFormData({ available_equipment: newEquipment });
    };

    // NEW: Toggle helpers for cardio and training prefs
    const toggleCardioActivity = (activity: string) => {
        const current = formData.cardio_preferred_activities || [];
        const updated = current.includes(activity)
            ? current.filter(a => a !== activity)
            : [...current, activity];
        updateFormData({ cardio_preferred_activities: updated });
    };

    const toggleWorkoutType = (type: string) => {
        const current = formData.preferred_workout_types || [];
        const updated = current.includes(type)
            ? current.filter(t => t !== type)
            : [...current, type];
        updateFormData({ preferred_workout_types: updated });
    };

    const togglePreferredDay = (day: number) => {
        const current = formData.preferred_days || [];
        const updated = current.includes(day)
            ? current.filter(d => d !== day)
            : [...current, day];
        updateFormData({ preferred_days: updated });
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold mb-2">Fitness Profile</h2>
                <p className="text-sm text-gray-600">Help us personalize your experience</p>
            </div>

            {/* Physical Condition */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    How would you describe your current physical condition?
                </label>
                <p className="text-xs text-gray-500 mb-3">This helps us create workouts that match where you are right now</p>
                <div className="space-y-2">
                    {[
                        { value: 'very_low', label: 'Very Low', desc: 'Mostly sedentary, get winded easily, limited physical activity' },
                        { value: 'low', label: 'Low', desc: 'Some activity but not consistent, easily fatigued' },
                        { value: 'moderate', label: 'Moderate', desc: 'Somewhat active, can handle everyday physical tasks' },
                        { value: 'good', label: 'Good', desc: 'Regularly active, decent stamina and strength' },
                        { value: 'high', label: 'High', desc: 'Very fit and active, strong physical capacity' }
                    ].map(level => (
                        <button
                            key={level.value}
                            type="button"
                            onClick={() => updateFormData({ physical_condition: level.value })}
                            className={`w-full p-3 rounded-lg border text-left ${formData.physical_condition === level.value
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <span className="font-medium">{level.label}</span>
                            <p className="text-xs text-gray-500 mt-0.5">{level.desc}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Fitness Goals */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    What are your fitness goals? (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-3">
                    {fitnessGoals.map(goal => (
                        <button
                            key={goal.value}
                            type="button"
                            onClick={() => toggleGoal(goal.value)}
                            className={`p-3 rounded-lg border text-left transition-colors ${(formData.fitness_goals || []).includes(goal.value)
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-xl">{goal.icon}</span>
                                <span className="text-sm font-medium">{goal.label}</span>
                            </div>
                        </button>
                    ))}
                </div>
                {errors.fitness_goals && (
                    <p className="text-red-500 text-xs mt-2">{errors.fitness_goals}</p>
                )}
            </div>

            {/* Fitness Level */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    Current fitness level
                </label>
                {errors.fitness_level && (
                    <p className="text-red-500 text-xs mb-2">{errors.fitness_level}</p>
                )}
                <div className="space-y-2">
                    {[
                        { value: 'beginner', label: 'Beginner', desc: 'New to exercise or returning after a long break' },
                        { value: 'intermediate', label: 'Intermediate', desc: 'Exercise regularly, comfortable with most movements' },
                        { value: 'advanced', label: 'Advanced', desc: 'Highly experienced, looking for challenging workouts' }
                    ].map(level => (
                        <button
                            key={level.value}
                            type="button"
                            onClick={() => updateFormData({ fitness_level: level.value })}
                            className={`w-full p-3 rounded-lg border text-left ${formData.fitness_level === level.value
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <span className="font-medium">{level.label}</span>
                            <p className="text-xs text-gray-500 mt-0.5">{level.desc}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* ============ NEW SECTIONS FOR EXERCISE PLATFORM ============ */}
            {isExercisePlatform && (
                <>
                    {/* Preferred Workout Types */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            What types of workouts do you enjoy?
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {workoutTypes.map(type => (
                                <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => toggleWorkoutType(type.value)}
                                    className={`px-3 py-2 rounded-full border text-sm transition-colors ${(formData.preferred_workout_types || []).includes(type.value)
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Cardio Preferences */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Preferred cardio activities
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {cardioActivities.map(activity => (
                                <button
                                    key={activity.value}
                                    type="button"
                                    onClick={() => toggleCardioActivity(activity.value)}
                                    className={`p-2 rounded-lg border text-left transition-colors ${(formData.cardio_preferred_activities || []).includes(activity.value)
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span>{activity.icon}</span>
                                        <span className="text-sm">{activity.label}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Indoor/Outdoor Preference */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Where do you prefer to exercise?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { value: 'indoor_only', label: 'Indoor' },
                                { value: 'outdoor_only', label: 'Outdoor' },
                                { value: 'both', label: 'Both' }
                            ].map(pref => (
                                <button
                                    key={pref.value}
                                    type="button"
                                    onClick={() => updateFormData({ outdoor_preference: pref.value })}
                                    className={`p-3 rounded-lg border text-center transition-colors ${formData.outdoor_preference === pref.value
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <span className="text-sm font-medium">{pref.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Preferred Days */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Which days work best for you?
                        </label>
                        <div className="flex gap-2">
                            {daysOfWeek.map(day => (
                                <button
                                    key={day.value}
                                    type="button"
                                    onClick={() => togglePreferredDay(day.value)}
                                    className={`flex-1 py-2 rounded-lg border text-center text-sm transition-colors ${(formData.preferred_days || []).includes(day.value)
                                        ? 'border-primary bg-primary/10 text-primary font-medium'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    {day.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sessions Per Week */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            How many sessions per week?
                        </label>
                        <div className="flex gap-2">
                            {[2, 3, 4, 5, 6, 7].map(num => (
                                <button
                                    key={num}
                                    type="button"
                                    onClick={() => updateFormData({ sessions_per_week: num })}
                                    className={`flex-1 py-3 rounded-lg border text-center transition-colors ${formData.sessions_per_week === num
                                        ? 'border-primary bg-primary/10 text-primary font-medium'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    {num}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Preferred Time of Day */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Preferred time of day
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { value: 'morning', label: '🌅 Morning' },
                                { value: 'afternoon', label: '☀️ Afternoon' },
                                { value: 'evening', label: '🌙 Evening' },
                                { value: 'flexible', label: '🔄 Flexible' }
                            ].map(time => (
                                <button
                                    key={time.value}
                                    type="button"
                                    onClick={() => updateFormData({ preferred_time_of_day: time.value })}
                                    className={`p-3 rounded-lg border text-center transition-colors ${formData.preferred_time_of_day === time.value
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <span className="text-sm">{time.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
            {/* ============ END NEW SECTIONS ============ */}

            {/* Preferred Duration */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    Preferred workout duration
                </label>
                <select
                    value={formData.preferred_duration || ''}
                    onChange={(e) => updateFormData({ preferred_duration: parseInt(e.target.value) })}
                    className="w-full p-3 border rounded-lg border-gray-200"
                >
                    <option value="">Select duration</option>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90+ minutes</option>
                </select>
            </div>

            {/* Available Equipment */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    Available equipment
                </label>
                <div className="space-y-2">
                    {equipmentOptions.map(equipment => (
                        <label
                            key={equipment}
                            className="flex items-center space-x-3 cursor-pointer p-2"
                        >
                            <input
                                type="checkbox"
                                checked={(formData.available_equipment || []).includes(equipment)}
                                onChange={() => toggleEquipment(equipment)}
                                className="rounded border-gray-300 text-primary"
                            />
                            <span className="text-sm">{equipment}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Physical Limitations - only show if NOT exercise platform (health screening covers this) */}
            {!isExercisePlatform && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Physical limitations or injuries (optional)
                    </label>
                    <textarea
                        value={formData.physical_limitations || ''}
                        onChange={(e) => updateFormData({ physical_limitations: e.target.value })}
                        className="w-full p-3 border rounded-lg border-gray-200"
                        rows={3}
                        placeholder="Any injuries or limitations we should know about?"
                    />
                </div>
            )}
        </div>
    );
};

const PaymentStep = ({
    onCheckout,
    onApplyCode,
    onRetryConfirm,
    loading,
    error,
    accessGranted,
    confirmState
}: {
    onCheckout: () => Promise<void>;
    onApplyCode: (code: string) => Promise<{ valid: boolean; message?: string }>;
    onRetryConfirm: () => void;
    loading: boolean;
    error: string | null;
    accessGranted: boolean;
    confirmState: 'idle' | 'confirming' | 'timeout';
}) => {
    const [accessCode, setAccessCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [codeSuccess, setCodeSuccess] = useState('');
    const [applyingCode, setApplyingCode] = useState(false);

    const handleApplyCode = async () => {
        if (!accessCode.trim()) return;

        setApplyingCode(true);
        setCodeError('');
        setCodeSuccess('');

        try {
            const result = await onApplyCode(accessCode.trim().toUpperCase());
            if (result.valid) {
                setCodeSuccess(result.message || 'Access code applied successfully!');
            } else {
                setCodeError(result.message || 'Invalid access code');
            }
        } catch (err) {
            setCodeError('Failed to validate code. Please try again.');
        } finally {
            setApplyingCode(false);
        }
    };

    // Back from Stripe Checkout: waiting for the webhook to grant access
    if (confirmState === 'confirming') {
        return (
            <div className="space-y-6 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
                <div>
                    <h2 className="text-xl font-semibold mb-2">Confirming your subscription...</h2>
                    <p className="text-gray-600">This usually takes a few seconds.</p>
                </div>
            </div>
        );
    }

    if (confirmState === 'timeout' && !accessGranted) {
        return (
            <div className="space-y-6 text-center">
                <div className="h-16 w-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                    <AlertTriangle className="h-8 w-8 text-yellow-600" />
                </div>
                <div>
                    <h2 className="text-xl font-semibold mb-2">Payment received, still confirming</h2>
                    <p className="text-gray-600">
                        Your subscription is being set up. Tap Retry to check again.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onRetryConfirm}
                    className="w-full py-3 px-4 bg-primary text-white rounded-lg font-medium hover:opacity-90"
                >
                    Retry
                </button>
            </div>
        );
    }

    // If access has been granted (via code or subscription), show success state
    if (accessGranted) {
        return (
            <div className="space-y-6 text-center">
                <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Check className="h-8 w-8 text-green-600" />
                </div>
                <div>
                    <h2 className="text-xl font-semibold mb-2">You're All Set!</h2>
                    <p className="text-gray-600">
                        Your access has been activated. Click below to start your fitness journey.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold mb-2">Start Your Free Trial</h2>
                <p className="text-sm text-gray-600">
                    7-day free trial, then $20/month. Cancel anytime from your profile.
                </p>
            </div>

            {/* Plan */}
            <div className="w-full p-4 rounded-lg border-2 border-primary bg-primary/5 text-left">
                <div className="flex justify-between items-start">
                    <div>
                        <span className="font-semibold">Ready2Exercise</span>
                        <p className="text-sm text-gray-500 mt-1">Monthly membership, billed after your trial</p>
                    </div>
                    <div className="text-right">
                        <span className="text-2xl font-bold">$20</span>
                        <span className="text-gray-500 text-sm">/month</span>
                    </div>
                </div>
            </div>

            {/* What's Included */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">What's included:</h3>
                <ul className="space-y-2">
                    {[
                        'Personalized workout programs',
                        'AI-powered exercise recommendations',
                        'Progress tracking & analytics',
                        'Video exercise demonstrations',
                        'Workout calendar & scheduling'
                    ].map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span>{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Checkout (Stripe collects the card; the trial converts automatically) */}
            <button
                onClick={onCheckout}
                disabled={loading}
                className="w-full py-3 px-4 bg-primary text-white rounded-lg font-medium transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
                {loading ? (
                    <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Processing...
                    </>
                ) : (
                    'Start 7-Day Free Trial'
                )}
            </button>

            <p className="text-center text-xs text-gray-500">
                You'll add a card on the next screen. Nothing is charged until your trial ends,
                and you can cancel before then at no cost.
            </p>

            {/* Access Code */}
            <div className="pt-4 border-t">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Have an access code?
                </label>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={accessCode}
                        onChange={(e) => {
                            setAccessCode(e.target.value.toUpperCase());
                            setCodeError('');
                            setCodeSuccess('');
                        }}
                        placeholder="Enter code"
                        className="flex-1 p-3 border rounded-lg border-gray-200 uppercase"
                        maxLength={20}
                    />
                    <button
                        onClick={handleApplyCode}
                        disabled={!accessCode.trim() || applyingCode}
                        className="px-4 py-2 bg-gray-100 rounded-lg font-medium text-sm hover:bg-gray-200 disabled:opacity-50"
                    >
                        {applyingCode ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            'Apply'
                        )}
                    </button>
                </div>
                {codeError && (
                    <p className="text-red-500 text-sm mt-1">{codeError}</p>
                )}
                {codeSuccess && (
                    <p className="text-green-600 text-sm mt-1">{codeSuccess}</p>
                )}
            </div>

            {/* Error Display */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}
        </div>
    );
};

const BiometricEnrollmentStep = ({
    onEnroll,
    onSkip,
    enrolling,
    enrollmentComplete,
    enrollmentError
}) => (
    <div className="space-y-6 text-center">
        <div>
            <h2 className="text-xl font-semibold mb-2">Secure Your Account</h2>
            <p className="text-sm text-gray-600">Add biometric authentication for enhanced security</p>
        </div>

        {enrolling ? (
            <div>
                <div className="rounded-full bg-blue-50 p-4 inline-flex mb-4">
                    <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                </div>
                <p className="text-gray-600">Please follow the on-screen instructions...</p>
            </div>
        ) : enrollmentComplete ? (
            <div>
                <div className="rounded-full bg-green-50 p-4 inline-flex mb-4">
                    <Check className="h-8 w-8 text-green-500" />
                </div>
                <p className="text-gray-600">Biometric enrollment complete!</p>
            </div>
        ) : enrollmentError ? (
            <div>
                <div className="rounded-full bg-red-50 p-4 inline-flex mb-4">
                    <AlertTriangle className="h-8 w-8 text-red-500" />
                </div>
                <p className="text-red-600 mb-4">{enrollmentError}</p>
                <button
                    onClick={onEnroll}
                    className="bg-primary text-white px-6 py-3 rounded-lg"
                >
                    Try Again
                </button>
            </div>
        ) : (
            <div>
                <div className="rounded-full bg-blue-50 p-4 inline-flex mb-4">
                    <Shield className="h-8 w-8 text-blue-500" />
                </div>

                <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                    <h3 className="font-medium mb-2">Benefits:</h3>
                    <ul className="space-y-1 text-sm text-gray-600">
                        <li>• Quick and secure login</li>
                        <li>• Enhanced account protection</li>
                        <li>• No passwords to remember</li>
                    </ul>
                </div>

                <div className="space-y-3">
                    <button
                        onClick={onEnroll}
                        className="w-full bg-primary text-white px-6 py-3 rounded-lg"
                    >
                        Enable Biometric Authentication
                    </button>
                    <button
                        onClick={onSkip}
                        className="w-full text-gray-500 text-sm"
                    >
                        Skip for now
                    </button>
                </div>
            </div>
        )}
    </div>
);

const TermsStep = ({
    formData,
    updateFormData,
    errors,
    isExercisePlatform = false  // NEW: pass this prop
}) => (
    <div className="space-y-6">
        <div>
            <h2 className="text-xl font-semibold mb-2">Terms & Agreements</h2>
            <p className="text-sm text-gray-600">Please review and accept our terms</p>
        </div>

        <div className="space-y-4">
            <div className="border rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formData.accepted_terms || false}
                        onChange={(e) => updateFormData({ accepted_terms: e.target.checked })}
                        className="mt-1 rounded border-gray-300 text-primary"
                    />
                    <div>
                        <span className="text-sm font-medium">Platform Terms of Use</span>
                        <p className="text-xs text-gray-500 mt-1">
                            I have read and agree to the{' '}
                            <a href="https://rehabilitationhealth.com/terms-of-use/" target="_blank" className="text-primary underline">
                                Terms of Use
                            </a>
                        </p>
                    </div>
                </label>
            </div>

            <div className="border rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formData.accepted_privacy || false}
                        onChange={(e) => updateFormData({ accepted_privacy: e.target.checked })}
                        className="mt-1 rounded border-gray-300 text-primary"
                    />
                    <div>
                        <span className="text-sm font-medium">Privacy Policy</span>
                        <p className="text-xs text-gray-500 mt-1">
                            I have read and agree to the{' '}
                            <a href="https://rehabilitationhealth.com/privacy-policy/" target="_blank" className="text-primary underline">
                                Privacy Policy
                            </a>
                        </p>
                    </div>
                </label>
            </div>

            {formData.isProviderLinked && (
                <div className="border rounded-lg p-4">
                    <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={formData.accepted_hipaa || false}
                            onChange={(e) => updateFormData({ accepted_hipaa: e.target.checked })}
                            className="mt-1 rounded border-gray-300 text-primary"
                        />
                        <div>
                            <span className="text-sm font-medium">HIPAA Consent</span>
                            <p className="text-xs text-gray-500 mt-1">
                                I consent to the secure handling of my health information
                            </p>
                        </div>
                    </label>
                </div>
            )}

            {isExercisePlatform && (
                <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                    <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={formData.accepted_health_disclaimer || false}
                            onChange={(e) => updateFormData({ accepted_health_disclaimer: e.target.checked })}
                            className="mt-1 rounded border-gray-300 text-primary"
                        />
                        <div>
                            <span className="text-sm font-medium text-amber-800">Health & Fitness Disclaimer</span>
                            <p className="text-xs text-amber-700 mt-1">
                                I understand that I should consult with a healthcare provider before starting
                                any new exercise program. I acknowledge that physical activity involves inherent
                                risks and I take full responsibility for my health and safety during exercise.
                            </p>
                        </div>
                    </label>
                    {errors.accepted_health_disclaimer && (
                        <p className="text-red-500 text-xs mt-2">{errors.accepted_health_disclaimer}</p>
                    )}
                </div>
            )}
        </div>

        {errors.terms && (
            <div className="bg-red-50 rounded-lg p-3">
                <p className="text-red-600 text-sm">{errors.terms}</p>
            </div>
        )}
    </div>
);

/**
 * Builds patient.fitness_profile from onboarding form data. Shared by onboarding submit and the
 * personalize page (which has no PAR-Q answers in formData and keeps the existing health_screening).
 */
export function buildFitnessProfile(
    formData: OnboardingFormData,
    existing: any,
    { exercisePlatform, includeHealthScreening }: { exercisePlatform: boolean; includeHealthScreening: boolean }
) {
    return {
        goals: formData.fitness_goals || [],
        fitness_level: formData.fitness_level || 'beginner',
        physical_condition: formData.physical_condition,
        preferred_duration: formData.preferred_duration || 30,
        available_equipment: formData.available_equipment || [],
        physical_limitations: formData.physical_limitations,
        created_at: existing?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),

        // NEW: Exercise platform specific fields
        ...(exercisePlatform && {
            cardio_preferences: {
                preferred_activities: formData.cardio_preferred_activities || [],
                outdoor_preference: formData.outdoor_preference || 'both',
                has_cardio_equipment: formData.available_equipment?.filter(e =>
                    ['Cardio Machine', 'Treadmill', 'Exercise Bike', 'Rowing Machine'].includes(e)
                ) || []
            },
            training_preferences: {
                preferred_workout_types: formData.preferred_workout_types || [],
                preferred_days: formData.preferred_days || [],
                preferred_time_of_day: formData.preferred_time_of_day || 'flexible',
                sessions_per_week: formData.sessions_per_week || 3
            },
            health_screening: !includeHealthScreening ? existing?.health_screening : {
                has_heart_condition: formData.has_heart_condition || false,
                has_chest_pain_during_activity: formData.has_chest_pain_during_activity || false,
                has_chest_pain_at_rest: formData.has_chest_pain_at_rest || false,
                has_balance_or_dizziness: formData.has_balance_or_dizziness || false,
                has_bone_or_joint_problems: formData.has_bone_or_joint_problems || false,
                takes_blood_pressure_medication: formData.takes_blood_pressure_medication || false,
                has_other_medical_reasons: formData.has_other_medical_reasons || false,
                other_medical_details: formData.other_medical_details,
                has_current_injuries: formData.has_current_injuries || false,
                current_injuries: formData.current_injuries || [],
                has_chronic_conditions: formData.has_chronic_conditions || false,
                chronic_conditions: formData.chronic_conditions || [],
                chronic_condition_details: (formData.chronic_condition_details || []).filter(d => d.details),
                physician_clearance_obtained: formData.physician_clearance_obtained || false,
                screening_passed: !formData.has_heart_condition && !formData.has_chest_pain_during_activity,
                requires_medical_clearance: formData.has_heart_condition || formData.has_chest_pain_during_activity || formData.takes_blood_pressure_medication,
                completed_at: new Date().toISOString()
            }
        })
    };
}

export function PatientOnboarding() {
    const navigate = useNavigate();
    const { patient: userProfile, loading, refreshPatient } = usePatient(); // 'patient' is actually UserProfile
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<OnboardingFormData>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const hasInitialized = useRef(false);

    // Biometric state
    const [enrolling, setEnrolling] = useState(false);
    const [enrollmentComplete, setEnrollmentComplete] = useState(false);
    const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
    const [faceTecInitialized, setFaceTecInitialized] = useState(false);

    // NEW: Payment state
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [accessGranted, setAccessGranted] = useState(false);

    // Return from Stripe Checkout (?payment=success|cancelled): restore the stashed form, land on the
    // Payment step, and on success wait for stripe/main-webhook before auto-completing onboarding.
    const [searchParams] = useSearchParams();
    const paymentReturn = searchParams.get('payment');
    const [confirmState, setConfirmState] = useState<'idle' | 'confirming' | 'timeout'>(
        paymentReturn === 'success' ? 'confirming' : 'idle'
    );
    const [autoSubmitPending, setAutoSubmitPending] = useState(false);
    const paymentJumpDone = useRef(false);
    const confirmRunning = useRef(false);

    // NEW: Platform detection
    const platform = getCurrentPlatform();
    const config = getEnvironmentConfig();
    const isExercisePlatform = platform.id === 'exercise';

    // Extract the actual patient data from the user profile
    const patientData = userProfile?.patient;
    const isProviderLinked = !!patientData?.provider_id || !!patientData?.organization_id;

    // Check if user already has exercise access
    useEffect(() => {
        if (userProfile?.exercise_app?.subscription_status) {
            const status = userProfile.exercise_app.subscription_status;
            if (['active', 'trial', 'access_code'].includes(status)) {
                setAccessGranted(true);
            }
        }
    }, [userProfile]);

    // Define steps based on user type and org config
    // Access granted before onboarding (referral code at signup) skips the Payment step.
    // Evaluated once on the first loaded profile so a mid-flow refresh (access code entered on the
    // Payment step) cannot shrink the step list while the user is standing on it.
    const [preGrantedAccess, setPreGrantedAccess] = useState<boolean | null>(null);
    // Static-link arrival: routine key persisted at signup (exercise_app.pending_routine_key), with the
    // sessionStorage copy as a fallback. Evaluated once for the same reason as preGrantedAccess.
    const [routineKey, setRoutineKey] = useState<string | null | undefined>(undefined);
    useEffect(() => {
        if (userProfile && preGrantedAccess === null) {
            setPreGrantedAccess(userProfile.exercise_app?.subscription_status === 'access_code');
            setRoutineKey(userProfile.exercise_app?.pending_routine_key || sessionStorage.getItem('pending_routine_key') || null);
        }
    }, [userProfile, preGrantedAccess]);

    const steps = useMemo(() => {
        if (isExercisePlatform) {
            // Routine arrivals skip the steps that only feed plan generation (Lifestyle, Fitness).
            const core = routineKey ? ['Profile', 'Health', 'Terms'] : ['Profile', 'Health', 'Lifestyle', 'Fitness', 'Terms'];
            if (!isProviderLinked && preGrantedAccess !== true) {
                return [...core, 'Payment'];
            }
            return core;
        }

        const allSteps = isProviderLinked
            ? ['Profile', 'Medical Info', 'Lifestyle', 'Fitness', 'Security', 'Terms']
            : ['Profile', 'Lifestyle', 'Fitness', 'Security', 'Terms'];

        const enabledStepKeys = userProfile?.organization?.settings?.patient_onboarding?.enabled_steps;

        if (!isProviderLinked || !enabledStepKeys || enabledStepKeys.length === 0) {
            return allSteps;
        }

        const stepKeyMap: Record<string, string> = {
            'Medical Info': 'medical_info',
            'Lifestyle': 'lifestyle',
            'Fitness': 'fitness',
            'Security': 'security',
        };

        return allSteps.filter(step => {
            if (step === 'Profile' || step === 'Terms') return true;
            const key = stepKeyMap[step];
            return key && enabledStepKeys.includes(key);
        });
    }, [isExercisePlatform, isProviderLinked, preGrantedAccess, routineKey, userProfile?.organization?.settings?.patient_onboarding]);

    // Stripe return: land on the Payment step once the step list is settled (routineKey resolved)
    useEffect(() => {
        if (!paymentReturn || paymentJumpDone.current || routineKey === undefined) return;
        const paymentIndex = steps.indexOf('Payment');
        if (paymentIndex >= 0) {
            paymentJumpDone.current = true;
            setCurrentStep(paymentIndex);
        }
    }, [paymentReturn, routineKey, steps]);

    // Poll /auth/me until stripe/main-webhook has written the subscription, then auto-complete onboarding
    const startConfirm = async () => {
        if (confirmRunning.current) return;
        confirmRunning.current = true;
        setConfirmState('confirming');
        const profile = await waitForExerciseSubscription();
        confirmRunning.current = false;
        if (profile) {
            setAccessGranted(true);
            await refreshPatient();
            setAutoSubmitPending(true);
        } else {
            setConfirmState('timeout');
        }
    };

    useEffect(() => {
        if (paymentReturn === 'success' && userProfile?.id) {
            startConfirm();
        }
    }, [paymentReturn, userProfile?.id]);

    // Submit once the subscription is confirmed and the restored form / routine key are in place
    useEffect(() => {
        if (autoSubmitPending && accessGranted && Object.keys(formData).length > 0 && routineKey !== undefined && !isSubmitting) {
            setAutoSubmitPending(false);
            handleSubmit();
        }
    }, [autoSubmitPending, accessGranted, formData, routineKey, isSubmitting]);

    useEffect(() => {
        const initializePatientRecord = async () => {
            if (userProfile && !loading && !hasInitialized.current) {
                hasInitialized.current = true;
                try {
                    await ensurePatientRecord();
                } catch (error) {
                    console.error('Failed to ensure patient record:', error);
                    hasInitialized.current = false; // Reset on error to allow retry
                }
            }
        };

        initializePatientRecord();
    }, [loading]);

    // Initialize form data with existing patient data
    useEffect(() => {
        if (patientData && Object.keys(formData).length === 0) {
            // Back from Stripe Checkout: the in-memory form (incl. Terms acceptance) was stashed before the redirect
            if (paymentReturn) {
                try {
                    const backup = sessionStorage.getItem(ONBOARDING_FORM_BACKUP_KEY);
                    if (backup) {
                        setFormData(JSON.parse(backup));
                        return;
                    }
                } catch (err) {
                    console.warn('Could not restore onboarding form backup:', err);
                }
            }
            setFormData({
                // Basic info
                first_name: patientData.first_name || '',
                last_name: patientData.last_name || '',
                phone: patientData.phone || '',
                preferred_pronouns: patientData.preferred_pronouns || '',

                // Notification preferences from patient record
                notification_preferences: patientData.notification_preferences ?? true,
                email_preferences: patientData.email_preferences ?? true,
                sms_preferences: patientData.sms_preferences ?? false,

                // MFA
                biometric_mfa: userProfile?.biometric_mfa || false,

                // Medical info
                allergies: patientData.allergies || [],
                current_medications: patientData.current_medications || [],
                medical_history: patientData.medical_history || [],

                // Emergency contact
                emergency_contact_name: patientData.emergency_contacts?.[0]?.name || '',
                emergency_contact_phone: patientData.emergency_contacts?.[0]?.phone || '',
                emergency_contact_relationship: patientData.emergency_contacts?.[0]?.relationship || '',

                // Lifestyle data (flattened)
                sleep_hours: patientData.lifestyle_data?.sleep?.average_hours,
                sleep_quality: patientData.lifestyle_data?.sleep?.quality,
                diet_tracking_level: patientData.lifestyle_data?.diet?.tracking_level || 'basic',
                eating_pattern: patientData.lifestyle_data?.diet?.eating_pattern,
                meals_per_day: patientData.lifestyle_data?.diet?.meals_per_day,
                water_intake: patientData.lifestyle_data?.diet?.water_intake,
                daily_calories: patientData.lifestyle_data?.diet?.daily_calories,
                protein_grams: patientData.lifestyle_data?.diet?.protein_grams,
                carbs_grams: patientData.lifestyle_data?.diet?.carbs_grams,
                fat_grams: patientData.lifestyle_data?.diet?.fat_grams,
                stress_level: patientData.lifestyle_data?.stress?.level,
                activity_level: patientData.lifestyle_data?.activity?.level,

                // Fitness profile
                fitness_goals: patientData.fitness_profile?.goals || [],
                fitness_level: patientData.fitness_profile?.fitness_level || '',
                physical_condition: patientData.fitness_profile?.physical_condition || undefined,
                preferred_duration: patientData.fitness_profile?.preferred_duration || 30,
                available_equipment: patientData.fitness_profile?.available_equipment || [],
                physical_limitations: patientData.fitness_profile?.physical_limitations || '',

                // Provider info (for display)
                provider_name: patientData.care_team?.find(ct => ct.role === 'primary')?.provider_name,
                organization_name: userProfile?.organization?.name,

                // Terms
                accepted_terms: false,
                accepted_privacy: false,
                accepted_hipaa: false,

                // Flag
                isProviderLinked
            });
        }
    }, [patientData, userProfile, isProviderLinked]);

    // Initialize FaceTec
    useEffect(() => {
        const initFaceTec = async () => {
            try {
                await initializeFaceTecSDK();
                setFaceTecInitialized(true);
            } catch (error) {
                console.error('Failed to initialize FaceTec:', error);
            }
        };
        initFaceTec();
    }, []);

    // Save progress after each step
    useEffect(() => {
        const saveProgress = async () => {
            // Don't save if we're in the middle of final submission
            if (currentStep > 0 && userProfile?.id && patientData?.id && !isSubmitting) {
                try {
                    // Save user onboarding progress
                    await patientApiService.updateMyProfile({
                        ...userProfile,
                        ...(formData.first_name && { first_name: formData.first_name }),
                        ...(formData.last_name && { last_name: formData.last_name }),
                        ...(formData.phone && { phone: formData.phone }),
                        onboarding: {
                            ...userProfile.onboarding,
                            completed_steps: steps.slice(0, currentStep),
                            last_step: steps[currentStep - 1],
                            is_completed: false
                        }
                    });

                    // Build complete patient update with ALL data collected so far
                    const patientUpdates: Patient = {
                        ...patientData, // Start with all existing data

                        // Basic info (always include if provided)
                        ...(formData.first_name && { first_name: formData.first_name }),
                        ...(formData.last_name && { last_name: formData.last_name }),
                        ...(formData.phone && { phone: formData.phone }),
                        ...(formData.preferred_pronouns && { preferred_pronouns: formData.preferred_pronouns }),

                        notification_preferences: formData.notification_preferences ?? patientData.notification_preferences,
                        email_preferences: formData.email_preferences ?? patientData.email_preferences,
                        sms_preferences: formData.sms_preferences ?? patientData.sms_preferences,

                        // Medical info (include if we're past that step and provider-linked)
                        ...(isProviderLinked && currentStep > steps.indexOf('Medical Info') && {
                            allergies: formData.allergies || patientData.allergies || [],
                            current_medications: formData.current_medications || patientData.current_medications || [],
                            medical_history: formData.medical_history || patientData.medical_history || [],
                            emergency_contacts: formData.emergency_contact_name ? [{
                                name: formData.emergency_contact_name,
                                phone: formData.emergency_contact_phone!,
                                relationship: formData.emergency_contact_relationship!,
                                is_primary: true
                            }] : patientData.emergency_contacts || []
                        }),

                        // Lifestyle data (include if we're past that step)
                        ...(currentStep > steps.indexOf('Lifestyle') && {
                            lifestyle_data: {
                                sleep: {
                                    average_hours: formData.sleep_hours || patientData.lifestyle_data?.sleep?.average_hours || 0,
                                    quality: formData.sleep_quality || patientData.lifestyle_data?.sleep?.quality || 'fair'
                                },
                                diet: {
                                    tracking_level: formData.diet_tracking_level || patientData.lifestyle_data?.diet?.tracking_level || 'none',
                                    ...(formData.diet_tracking_level === 'basic' && {
                                        eating_pattern: (formData.eating_pattern as 'regular' | 'irregular' | 'restricted') || patientData.lifestyle_data?.diet?.eating_pattern
                                    }),
                                    ...(formData.diet_tracking_level === 'detailed' && {
                                        meals_per_day: formData.meals_per_day || patientData.lifestyle_data?.diet?.meals_per_day,
                                        water_intake: formData.water_intake || patientData.lifestyle_data?.diet?.water_intake
                                    }),
                                    ...(formData.diet_tracking_level === 'macros' && {
                                        daily_calories: formData.daily_calories || patientData.lifestyle_data?.diet?.daily_calories,
                                        protein_grams: formData.protein_grams || patientData.lifestyle_data?.diet?.protein_grams,
                                        carbs_grams: formData.carbs_grams || patientData.lifestyle_data?.diet?.carbs_grams,
                                        fat_grams: formData.fat_grams || patientData.lifestyle_data?.diet?.fat_grams
                                    })
                                },
                                stress: {
                                    level: formData.stress_level || patientData.lifestyle_data?.stress?.level || 'moderate'
                                },
                                activity: {
                                    level: formData.activity_level || patientData.lifestyle_data?.activity?.level || 'lightly_active'
                                },
                                updated_at: new Date().toISOString()
                            }
                        }),

                        // Fitness profile (include if we're past that step)
                        ...(currentStep > steps.indexOf('Fitness') && {
                            fitness_profile: {
                                goals: (formData.fitness_goals as ("weight_loss" | "muscle_gain" | "endurance" | "strength" | "flexibility" | "general_fitness")[]) || patientData.fitness_profile?.goals || [],
                                fitness_level: (formData.fitness_level as "beginner" | "intermediate" | "advanced") || patientData.fitness_profile?.fitness_level || 'beginner',
                                preferred_duration: formData.preferred_duration || patientData.fitness_profile?.preferred_duration || 30,
                                available_equipment: formData.available_equipment || patientData.fitness_profile?.available_equipment || [],
                                physical_limitations: formData.physical_limitations || patientData.fitness_profile?.physical_limitations,
                                created_at: patientData.fitness_profile?.created_at || new Date().toISOString(),
                                updated_at: new Date().toISOString()
                            }
                        }),

                        // Communication preferences (always include if provided)
                        ...(formData.notification_preferences !== undefined && {
                            communication_preferences: {
                                appointment_reminders: formData.notification_preferences,
                                health_tips: formData.email_preferences || false,
                                billing_notifications: formData.email_preferences || false,
                                lab_results: isProviderLinked && formData.notification_preferences,
                                prescription_refills: isProviderLinked && formData.notification_preferences
                            }
                        })
                    };

                    await patientApiService.updatePatientRecord(patientData.id, patientUpdates);
                } catch (error) {
                    console.error('Failed to save progress:', error);
                    // Don't throw - this is a background save
                }
            }
        };

        saveProgress();
    }, [currentStep, userProfile, patientData, steps, isProviderLinked, isSubmitting]);

    // Payment handlers
    // Stripe Checkout: $20/month subscription with a 7-day trial (card collected by Stripe).
    // The form is stashed first because the redirect unmounts the page; it is restored on return.
    const handleCheckout = async () => {
        try {
            setPaymentLoading(true);
            setPaymentError(null);

            try {
                sessionStorage.setItem(ONBOARDING_FORM_BACKUP_KEY, JSON.stringify(formData));
            } catch (err) {
                console.warn('Could not stash onboarding form:', err);
            }

            const result = await patientApiService.createExerciseCheckoutSession(userProfile?.email, '/onboarding');

            if (result.checkout_url) {
                // Redirect to Stripe Checkout
                window.location.href = result.checkout_url;
            } else {
                setPaymentError('Failed to create checkout session');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            setPaymentError('Failed to process payment. Please try again.');
        } finally {
            setPaymentLoading(false);
        }
    };

    const handleApplyAccessCode = async (code: string): Promise<{ valid: boolean; message?: string }> => {
        try {
            const result = await patientApiService.validateExerciseAccessCode(code, userProfile?.id);

            if (result.valid && result.type === 'full_access') {
                // Code grants full access - mark as granted
                setAccessGranted(true);
                await refreshPatient();
                return { valid: true, message: 'Access granted!' };
            }

            // Discounts are Stripe promotion codes now, entered on the Checkout page
            return { valid: false, message: 'Invalid or expired code' };
        } catch (error) {
            return { valid: false, message: 'Failed to validate code' };
        }
    };

    const updateFormData = (updates: Partial<OnboardingFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const ensurePatientRecord = async () => {
        if (!userProfile?.patient && userProfile?.id) {
            try {
                // Create a patient record for this user
                const newPatient = await patientApiService.createPatientRecord({
                    user_id: userProfile.id,
                    first_name: userProfile.first_name || '',
                    last_name: userProfile.last_name || '',
                    email: userProfile.email,
                    phone: userProfile.phone || '',
                    provider_id: null, // Standalone user
                    organization_id: null,
                    status: 'active'
                });

                // Check for pending program to link
                const pendingProgramId = sessionStorage.getItem('pending_program_id');
                if (pendingProgramId && newPatient?.id) {
                    try {
                        const linkResult = await patientApiService.linkProgramToUser(pendingProgramId, newPatient.id);

                        if (linkResult.success) {
                            sessionStorage.removeItem('pending_program_id');
                        } else {
                            console.error('Failed to link program during onboarding:', linkResult.error);
                            sessionStorage.removeItem('pending_program_id');
                        }
                    } catch (linkError) {
                        console.error('Error linking program during onboarding:', linkError);
                        sessionStorage.removeItem('pending_program_id');
                    }
                }

                // Check for pending workout plan to link
                // Note: don't clear sessionStorage here — handleSubmit needs it for the auto-generation skip check
                const pendingWorkoutPlanId = sessionStorage.getItem('pending_workout_plan_id');
                if (pendingWorkoutPlanId && newPatient?.id) {
                    try {
                        const linkResult = await patientApiService.linkWorkoutPlanToUser(pendingWorkoutPlanId);

                        if (!linkResult.success) {
                            console.error('Failed to link workout plan during onboarding:', linkResult.error);
                        }
                    } catch (linkError) {
                        console.error('Error linking workout plan during onboarding:', linkError);
                    }
                }

                // Refresh to get the updated user profile with patient
                await refreshPatient();
                return newPatient;
            } catch (error) {
                console.error('Failed to create patient record:', error);
                throw error;
            }
        }

        // If patient already exists, check if there's still a pending program
        const pendingProgramId = sessionStorage.getItem('pending_program_id');
        if (pendingProgramId && userProfile?.patient?.id) {
            try {
                const linkResult = await patientApiService.linkProgramToUser(pendingProgramId);

                if (linkResult.success) {
                    sessionStorage.removeItem('pending_program_id');
                } else {
                    console.error('Failed to link program for existing patient:', linkResult.error);
                    sessionStorage.removeItem('pending_program_id');
                }
            } catch (linkError) {
                console.error('Error linking program for existing patient:', linkError);
                sessionStorage.removeItem('pending_program_id');
            }
        }

        // Check for pending workout plan
        // Note: don't clear sessionStorage here — handleSubmit needs it for the auto-generation skip check
        const pendingWorkoutPlanId2 = sessionStorage.getItem('pending_workout_plan_id');
        if (pendingWorkoutPlanId2 && userProfile?.patient?.id) {
            try {
                const linkResult = await patientApiService.linkWorkoutPlanToUser(pendingWorkoutPlanId2);

                if (!linkResult.success) {
                    console.error('Failed to link workout plan for existing patient:', linkResult.error);
                }
            } catch (linkError) {
                console.error('Error linking workout plan for existing patient:', linkError);
            }
        }

        return userProfile?.patient;
    };

    const validateStep = () => {
        const stepName = steps[currentStep];
        const newErrors: Record<string, string> = {};

        switch (stepName) {
            case 'Profile':
                if (!formData.first_name?.trim()) newErrors.first_name = 'First name is required';
                if (!formData.last_name?.trim()) newErrors.last_name = 'Last name is required';
                if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
                break;

            case 'Health':
                // Health screening is informational, but ensure questions are answered
                const parqQuestions = [
                    'has_heart_condition', 'has_chest_pain_during_activity', 'has_chest_pain_at_rest',
                    'has_balance_or_dizziness', 'has_bone_or_joint_problems', 'takes_blood_pressure_medication',
                    'has_other_medical_reasons'
                ];
                const unanswered = parqQuestions.filter(q => formData[q as keyof OnboardingFormData] === undefined);
                if (unanswered.length > 0) {
                    newErrors.health_screening = 'Please answer all health screening questions';
                }
                break;

            case 'Fitness':
                if (!formData.fitness_goals?.length) newErrors.fitness_goals = 'Please select at least one goal';
                if (!formData.fitness_level) newErrors.fitness_level = 'Please select your fitness level';
                break;

            case 'Terms':
                if (!formData.accepted_terms) newErrors.accepted_terms = 'You must accept the terms';
                if (!formData.accepted_privacy) newErrors.accepted_privacy = 'You must accept the privacy policy';
                if (isExercisePlatform && !formData.accepted_health_disclaimer) {
                    newErrors.accepted_health_disclaimer = 'You must accept the health disclaimer';
                }
                if (isProviderLinked && !formData.accepted_hipaa) {
                    newErrors.accepted_hipaa = 'You must accept HIPAA consent';
                }
                break;

            case 'Payment':
                // Payment step doesn't require validation - user can proceed when access is granted
                if (!accessGranted) {
                    newErrors.payment = 'Please complete payment or start a trial';
                }
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep()) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
    };

    const handleBiometricEnroll = async () => {
        try {
            setEnrolling(true);
            setEnrollmentError(null);

            // Get session token
            const { faceTecApp } = await import('@prism-ehr/shared/services/faceTecService');
            faceTecApp.getSessionToken((sessionToken) => {
                if (sessionToken) {
                    const enrollmentProcessor = new EnrollmentProcessor(
                        sessionToken,
                        userProfile?.id,
                        (result) => {
                            if (result.success) {
                                setEnrollmentComplete(true);
                                // ADD THIS LINE to track biometric enrollment
                                updateFormData({ biometric_mfa: true });
                                setTimeout(() => handleNext(), 1500);
                            } else {
                                setEnrollmentError('Enrollment failed. Please try again.');
                            }
                            setEnrolling(false);
                        }
                    );
                } else {
                    setEnrollmentError('Failed to initialize biometric system');
                    setEnrolling(false);
                }
            });
        } catch (error) {
            console.error('Enrollment error:', error);
            setEnrollmentError('Enrollment failed. Please try again.');
            setEnrolling(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateStep()) return;

        try {
            setIsSubmitting(true);

            // Update patient record
            if (patientData?.id) {
                const patientUpdateData: any = {
                    ...patientData,
                    first_name: formData.first_name!,
                    last_name: formData.last_name!,
                    phone: formData.phone,
                    // ... existing fields ...

                    // NEW: Extended fitness profile for exercise platform
                    fitness_profile: buildFitnessProfile(formData, patientData.fitness_profile, { exercisePlatform: isExercisePlatform, includeHealthScreening: true })
                };

                await patientApiService.updatePatientRecord(patientData.id, patientUpdateData);
            }

            // Update user profile
            const timestamp = new Date().toISOString();
            const userUpdateData: any = {
                ...userProfile,
                biometric_mfa: formData.biometric_mfa || false,
                terms: {
                    ...userProfile?.terms,
                    platform_terms: formData.accepted_terms ? { version: "1.0.0", accepted_at: timestamp } : userProfile?.terms?.platform_terms,
                    privacy_policy: formData.accepted_privacy ? { version: "1.0.0", accepted_at: timestamp } : userProfile?.terms?.privacy_policy,
                    hipaa_consent: isProviderLinked && formData.accepted_hipaa ? { version: "1.0.0", accepted_at: timestamp } : userProfile?.terms?.hipaa_consent
                }
            };

            // NEW: Platform-specific onboarding completion
            if (isExercisePlatform) {
                const freshProfile = await patientApiService.getMe();
                userUpdateData.exercise_app = {
                    ...freshProfile?.exercise_app,
                    onboarding_completed: true,
                    onboarding_completed_at: timestamp,
                    health_disclaimer_accepted: formData.accepted_health_disclaimer || false,
                    health_disclaimer_accepted_at: formData.accepted_health_disclaimer ? timestamp : undefined,
                    updated_at: timestamp
                };
            } else {
                userUpdateData.onboarding = {
                    is_completed: true,
                    completed_steps: steps,
                    completed_at: timestamp
                };
            }

            await patientApiService.updateMyProfile(userUpdateData);
            await refreshPatient();
            sessionStorage.removeItem(ONBOARDING_FORM_BACKUP_KEY);

            if (isExercisePlatform) {
                const hasPendingPlan = sessionStorage.getItem('pending_workout_plan_id');
                if (!hasPendingPlan) {
                    // Referral link with a routine: assign the static base routine. Otherwise build a
                    // personalized plan from the curated ladder (one AI call, a few seconds).
                    const pendingRoutineKey = routineKey || sessionStorage.getItem('pending_routine_key');
                    let planResult: { assignment_id?: string } | null = null;
                    try {
                        planResult = pendingRoutineKey
                            ? await patientApiService.claimBaseRoutine(pendingRoutineKey)
                            : await patientApiService.generatePlan();
                    } catch (error) {
                        console.error('Failed to create initial workout plan:', error);
                        setErrors({ submit: 'Your answers are saved, but we could not build your plan. Tap Complete Setup to try again.' });
                        return;
                    }
                    sessionStorage.removeItem('pending_routine_key');
                    sessionStorage.removeItem('pending_referral_code');
                    if (planResult?.assignment_id) {
                        navigate(`/programs/${planResult.assignment_id}`);
                        return;
                    }
                }
                sessionStorage.removeItem('pending_workout_plan_id');
                navigate('/programs');
            } else {
                navigate('/dashboard');
            }

        } catch (error) {
            console.error('Failed to complete onboarding:', error);
            setErrors({ submit: 'Failed to save your information. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };


    const renderStep = () => {
        const stepName = steps[currentStep];

        switch (stepName) {
            case 'Profile':
                return (
                    <ProfileBasicsStep
                        formData={formData}
                        updateFormData={updateFormData}
                        errors={errors}
                        isProviderLinked={isProviderLinked}
                    />
                );

            case 'Health':
                return (
                    <HealthScreeningStep
                        formData={formData}
                        updateFormData={updateFormData}
                        errors={errors}
                    />
                );

            case 'Medical Info':
                return (
                    <MedicalInfoStep
                        formData={formData}
                        updateFormData={updateFormData}
                        errors={errors}
                    />
                );

            case 'Lifestyle':
                return (
                    <LifestyleStep
                        formData={formData}
                        updateFormData={updateFormData}
                        errors={errors}
                    />
                );

            case 'Fitness':
                return (
                    <FitnessProfileStep
                        formData={formData}
                        updateFormData={updateFormData}
                        errors={errors}
                        isExercisePlatform={isExercisePlatform}  // NEW prop
                    />
                );

            case 'Security':
                return (
                    <BiometricEnrollmentStep
                        onEnroll={handleBiometricEnroll}
                        onSkip={handleNext}
                        enrolling={enrolling}
                        enrollmentComplete={enrollmentComplete}
                        enrollmentError={enrollmentError}
                    />
                );

            case 'Terms':
                return (
                    <TermsStep
                        formData={formData}
                        updateFormData={updateFormData}
                        errors={errors}
                        isExercisePlatform={isExercisePlatform}  // NEW prop
                    />
                );

            case 'Payment':
                return (
                    <PaymentStep
                        onCheckout={handleCheckout}
                        onApplyCode={handleApplyAccessCode}
                        onRetryConfirm={startConfirm}
                        loading={paymentLoading}
                        error={paymentError}
                        accessGranted={accessGranted}
                        confirmState={confirmState}
                    />
                );

            default:
                return null;
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-16 lg:pb-0">
            <div className="max-w-lg mx-auto p-4">
                {/* Progress bar */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-bold">Welcome!</h1>
                        <span className="text-sm text-gray-500">
                            Step {currentStep + 1} of {steps.length}
                        </span>
                    </div>

                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Step content */}
                <div className="bg-white rounded-lg p-6 shadow-sm">
                    {renderStep()}
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-6">
                    {currentStep > 0 && (
                        <button
                            onClick={handleBack}
                            className="flex items-center gap-2 text-gray-600"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </button>
                    )}

                    {currentStep < steps.length - 1 ? (
                        <button
                            onClick={handleNext}
                            disabled={
                                (currentStep === steps.indexOf('Security') && enrolling) ||
                                (currentStep === steps.indexOf('Payment') && !accessGranted)
                            }
                            className="ml-auto bg-primary text-white px-6 py-3 rounded-lg disabled:opacity-50"
                        >
                            Continue
                        </button>
                    ) : (
                        <div className="ml-auto flex flex-col items-end gap-2">
                            {errors.submit && (
                                <p className="text-sm text-red-600 text-right max-w-xs">{errors.submit}</p>
                            )}
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting || (steps.includes('Payment') && !accessGranted)}
                                className="bg-primary text-white px-6 py-3 rounded-lg disabled:opacity-50 flex items-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {isExercisePlatform ? 'Building your plan...' : 'Saving...'}
                                    </>
                                ) : (
                                    'Complete Setup'
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}